import { Button } from "@/components/ui/button";
import useBreakpoint from "@/hooks/use-breakpoint";
import { CLEAR_FLAG } from "@/lib/clear-flag";
import { filterChipLabelKey, formatChipIds } from "@/lib/filter-chip-label";
import { cn } from "@/lib/utils";
import type { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTableFilters, useTableQueryArgument } from "./data-table-context";

type DataTableActiveFiltersProps = {
  disable?: boolean;
  className?: string;
};

const isDefault = (value: unknown, def: unknown) =>
  Array.isArray(def) ? def.includes(value) : value === def;

const isIdFilterKey = (key: string) => /Ids?$/.test(key);

const formatFilterValue = (value: unknown, t: TFunction): string => {
  if (Array.isArray(value)) {
    return value.map((item) => formatFilterValue(item, t)).join(", ");
  }
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "";
  }
  if (typeof value === "boolean") {
    return value ? t("booleanTrue") : t("booleanFalse");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

export function DataTableActiveFilters({
  disable,
  className,
}: DataTableActiveFiltersProps) {
  const isMobile = !useBreakpoint("md");
  const { t } = useTranslation();
  const [args, setArgs] = useTableQueryArgument();
  const { defaultFilters, defaultFiltersApply, filterBreadcrumbs } =
    useTableFilters();

  // An id key that names entities has a formatter that looks the names up (filter-chip-lookups.tsx);
  // without one, the chip shows the ids as they are. It used to guess names from whatever the React
  // Query cache held when it rendered, matching query keys by substring: a link opened cold showed
  // the raw id, and a sort could land on the table's own rows and show another entity's name
  // (ID-7084).
  const resolveFilterValue = React.useCallback(
    (key: string, value: unknown): React.ReactNode =>
      isIdFilterKey(key)
        ? formatChipIds(value, t("unassigned"))
        : formatFilterValue(value, t),
    [t],
  );

  const activeFilters = React.useMemo(() => {
    if (!defaultFilters) {
      return [] as {
        key: string;
        label: string;
        value: React.ReactNode;
      }[];
    }

    const hiddenKeys = new Set(
      (filterBreadcrumbs?.hiddenKeys ?? []) as string[],
    );

    return Object.entries(defaultFilters)
      .filter(([key]) => !hiddenKeys.has(key))
      .filter(([key, def]) => !isDefault((args as any)?.[key], def))
      .map(([key]) => {
        const formatValue = (filterBreadcrumbs?.formatters as any)?.[key] as
          | ((value: unknown) => React.ReactNode)
          | undefined;
        const currentValue = (args as any)?.[key];
        return {
          key,
          label: t(filterChipLabelKey(key, filterBreadcrumbs?.labelKeys)),
          value: formatValue
            ? formatValue(currentValue)
            : resolveFilterValue(key, currentValue),
        };
      })
      .filter(({ value }) => value !== "");
  }, [args, defaultFilters, filterBreadcrumbs, resolveFilterValue, t]);

  const removeFilter = React.useCallback(
    (key: string) => {
      setArgs((prev) => ({
        ...prev,
        [key]: (defaultFiltersApply as any)?.[key],
        page: 1,
        [CLEAR_FLAG]: true,
      }));
    },
    [defaultFiltersApply, setArgs],
  );

  const clearAllFilters = React.useCallback(() => {
    setArgs((prev) => ({
      ...prev,
      ...(defaultFiltersApply ?? {}),
      page: 1,
      [CLEAR_FLAG]: true,
    }));
  }, [defaultFiltersApply, setArgs]);

  if (disable || isMobile || activeFilters.length <= 0) {
    return null;
  }

  return (
    <div
      data-component="data-table-active-filters"
      className={cn("flex items-center gap-2 flex-wrap pb-2", className)}
    >
      <span className="text-sm text-muted-foreground">
        {t("activeFilters")}
      </span>
      <div className="flex items-center gap-2 flex-wrap" id="chipContainer">
        {activeFilters.map((filter) => (
          <span
            key={filter.key}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-sm"
          >
            <span className="text-muted-foreground">{filter.label}:</span>
            <span className="font-medium">{filter.value}</span>
            <button
              type="button"
              data-chip={filter.key}
              aria-label={t("removeFilter", { filter: filter.label })}
              className="text-muted-foreground hover:text-foreground"
              onClick={() => removeFilter(filter.key)}
            >
              x
            </button>
          </span>
        ))}
      </div>
      <Button
        id="clearAll"
        type="button"
        variant="link"
        className="h-auto p-0 text-link"
        onClick={clearAllFilters}
      >
        {t("clearAll")}
      </Button>
    </div>
  );
}
